import React, { memo, useState, useEffect } from 'react';
import { Receipt, SettingItem, Currency } from '../types';
import { useSettings } from '../hooks/useSettings';
import { SearchableSelect } from './SearchableSelect';
import { CodeIcon } from './icons/CodeIcon';
import { DeleteIcon } from './icons/DeleteIcon';
import { FileIcon } from './icons/FileIcon';
import { formatDate, getContrastColor, formatDateForInput } from '../services/utils';
import { logger } from '../services/logger';

interface ReceiptRowProps {
    receipt: Receipt;
    isEditMode: boolean;
    isSelected: boolean;
    onUpdate: (receipt: Receipt) => void;
    onDelete: (receipt: Receipt) => void;
    onToggleSelect: (id: string) => void;
    onViewJson: (json: string) => void;
    onViewFile: (receipt: Receipt) => void;
}

const MemoizedReceiptRow: React.FC<ReceiptRowProps> = ({
    receipt,
    isEditMode,
    isSelected,
    onUpdate,
    onDelete,
    onToggleSelect,
    onViewJson,
    onViewFile,
}) => {
    const settings = useSettings();
    const [amountValue, setAmountValue] = useState<string>(
        receipt.amount === undefined || receipt.amount === null ? '' : String(receipt.amount)
    );

    useEffect(() => {
        setAmountValue(
            receipt.amount === undefined || receipt.amount === null ? '' : String(receipt.amount)
        );
    }, [receipt.amount]);


    const handleFieldChange = <K extends keyof Receipt>(field: K, value: Receipt[K]) => {
        logger.debug(`[ReceiptRow] Pole '${String(field)}' se mění na:`, value);
        onUpdate({ ...receipt, [field]: value });
    };

    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAmountValue(e.target.value);
    };

    const handleAmountBlur = () => {
        const parsedAmount = parseFloat(amountValue.replace(',', '.'));
        
        if (isNaN(parsedAmount)) {
            if (amountValue.trim() === '') {
                // Uživatel vymazal pole
                if (receipt.amount !== null && receipt.amount !== undefined) {
                    logger.debug(`[ReceiptRow] Částka vymazána. Původní hodnota: ${receipt.amount}. Volám handleFieldChange s 'null'.`);
                    handleFieldChange('amount', null as any);
                }
            } else {
                // Neplatný vstup, vrátíme se k původní hodnotě
                logger.debug(`[ReceiptRow] Neplatná hodnota částky "${amountValue}". Vracím zpět na původní hodnotu.`);
                setAmountValue(receipt.amount === undefined || receipt.amount === null ? '' : String(receipt.amount));
            }
        } else {
            // Platný vstup
            if (parsedAmount !== receipt.amount) {
                logger.debug(`[ReceiptRow] Částka změněna z ${receipt.amount} na ${parsedAmount}.`);
                handleFieldChange('amount', parsedAmount as any);
            }
        }
    };

    return (
        <tr className={`transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
            {isEditMode && (
                <td className="px-6 py-4">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggleSelect(receipt.id)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        aria-label={`Vybrat účtenku od ${receipt.businessName}`}
                    />
                </td>
            )}
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {isEditMode ? (
                    <input
                        type="date"
                        value={formatDateForInput(receipt.receiptDate)}
                        onChange={(e) => handleFieldChange('receiptDate', e.target.value)}
                        className="p-1 border border-gray-300 rounded-md w-full"
                    />
                ) : (
                    formatDate(receipt.receiptDate)
                )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {isEditMode ? (
                    <input
                        type="text"
                        value={receipt.businessName || ''}
                        onChange={(e) => handleFieldChange('businessName', e.target.value)}
                        className="p-1 border border-gray-300 rounded-md w-full"
                    />
                ) : (
                    receipt.businessName || '-'
                )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {isEditMode ? (
                    <div className="flex items-center space-x-2">
                        <input
                            type="number"
                            step="any"
                            value={amountValue}
                            onChange={handleAmountChange}
                            onBlur={handleAmountBlur}
                            className="p-1 border border-gray-300 rounded-md w-24"
                        />
                        <div className="w-24">
                            <SearchableSelect
                                value={receipt.currency}
                                onChange={(newValue) => handleFieldChange('currency', newValue as Currency)}
                                options={settings.currencies}
                                placeholder="Měna"
                                onAddItem={(item) => settings.addItem('currencies', item)}
                            />
                        </div>
                    </div>
                ) : (
                    `${receipt.amount?.toLocaleString('cs-CZ') || '-'} ${receipt.currency.name || ''}`
                )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {isEditMode ? (
                    <SearchableSelect
                        value={receipt.category}
                        onChange={(newValue) => handleFieldChange('category', newValue as SettingItem)}
                        options={settings.categories}
                        placeholder="Kategorie"
                        onAddItem={(item) => settings.addItem('categories', item)}
                    />
                ) : (
                    <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full`}
                        style={{
                            backgroundColor: receipt.category.color || '#F3F4F6',
                            color: getContrastColor(receipt.category.color)
                        }}
                    >
                        {receipt.category.name}
                    </span>
                )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {isEditMode ? (
                    <SearchableSelect
                        value={receipt.paymentMethod}
                        onChange={(newValue) => handleFieldChange('paymentMethod', newValue as SettingItem)}
                        options={settings.paymentMethods}
                        placeholder="Způsob platby"
                        onAddItem={(item) => settings.addItem('paymentMethods', item)}
                    />
                ) : (
                    <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full`}
                        style={{
                            backgroundColor: receipt.paymentMethod.color || '#F3F4F6',
                            color: getContrastColor(receipt.paymentMethod.color)
                        }}
                    >
                        {receipt.paymentMethod.name}
                    </span>
                )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {isEditMode ? (
                    <SearchableSelect
                        isMulti
                        value={receipt.tags || []}
                        onChange={(newValue) => handleFieldChange('tags', newValue as SettingItem[])}
                        options={settings.tags}
                        placeholder="Přidat štítky..."
                        onAddItem={(item) => settings.addItem('tags', item)}
                    />
                ) : (
                    <div className="flex flex-wrap gap-1">
                        {(receipt.tags || []).map(tag => (
                            <span
                                key={tag.id}
                                className={`px-2 py-0.5 text-xs font-medium rounded-full`}
                                style={{ backgroundColor: tag.color, color: getContrastColor(tag.color) }}
                            >
                                {tag.name}
                            </span>
                        ))}
                    </div>
                )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end space-x-4">
                    {!isEditMode && (receipt.fileBlob || receipt.fileUrl) ? (
                        <button
                            onClick={() => onViewFile(receipt)}
                            title={`Zobrazit soubor: ${receipt.fileName}`}
                            className="text-blue-600 hover:text-blue-800"
                        >
                            <FileIcon className="w-5 h-5" />
                        </button>
                    ) : !isEditMode && (
                        <span title={`${receipt.fileName}\n(soubor není k dispozici)`}><FileIcon className="w-5 h-5 text-gray-400" /></span>
                    )}
                    <button
                        onClick={() => onViewJson(receipt.jsonData)}
                        className="text-gray-500 hover:text-blue-600 transition-colors"
                        title="Zobrazit JSON data"
                    >
                        <CodeIcon className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => onDelete(receipt)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                        title="Smazat účtenku"
                    >
                        <DeleteIcon className="w-5 h-5" />
                    </button>
                </div>
            </td>
        </tr>
    );
};

export const ReceiptRow = memo(MemoizedReceiptRow);